import { ChartCalculateMode } from '@dbl/core/chartEditor';
import type {
  IMapSearchViewModelContext,
  ISearchConnectedProps,
  ISearchModelState,
  ISearchViewModel,
} from '../types';
import { namespace } from '../../../model';

/** 从全局 state 读取 Analysis/MultiAnalysis model 切片 */
export const selectSearchModelState = (state: {
  [namespace]?: ISearchModelState;
}): ISearchModelState => {
  const model = state[namespace];
  return {
    dataTables: model?.dataTables ?? [],
    columnFilters: model?.columnFilters ?? [],
    columnUserFilters: model?.columnUserFilters ?? [],
    columnCompares: model?.columnCompares ?? [],
    columnMetrics: model?.columnMetrics ?? [],
    columnDimensions: model?.columnDimensions ?? [],
    columnX: model?.columnX ?? [],
    columnShowDimensions: model?.columnShowDimensions ?? [],
    customDimensionMap: model?.customDimensionMap ?? {
      options: [],
      data: [],
      loading: false,
    },
    columnRawColumns: model?.columnRawColumns ?? [],
    mode: model?.mode ?? ChartCalculateMode.Aggregate,
    dragState: model?.dragState ?? { currentDrag: '', errorDrag: {} },
    saveObj: model?.saveObj ?? {
      name: '',
      nameCn: '',
      label: '',
      business_type: 0,
      type: 0,
    },
    task_id: model?.task_id ?? '',
    hashEqualForAnalysis: model?.hashEqualForAnalysis ?? true,
    analysisRunning: model?.analysisRunning ?? false,
    chartType: model?.chartType ?? 'Table',
    rowLimit: model?.rowLimit ?? 5000,
    save_column_infos: model?.save_column_infos ?? {},
    customColumns: model?.customColumns ?? [],
    dataTableJoinRelations: model?.dataTableJoinRelations ?? [],
    system: model?.system ?? 'datalab',
    hasUserFilters: model?.hasUserFilters ?? false,
    visibleSearch: model?.visibleSearch ?? true,
  };
};

/**
 * model 原始 key → 视图语义 fields.*
 * 后期 model 重构时仅改此映射与 adapter 的 FIELD_ZONE_MODEL_KEY
 */
export const mapSearchViewModel = (
  model: ISearchModelState,
  ctx: IMapSearchViewModelContext,
): ISearchViewModel => ({
  isRawColumnMode: ctx.isRawColumnMode,
  fields: {
    filters: model.columnFilters,
    userFilters: model.columnUserFilters,
    compares: model.columnCompares,
    metrics: model.columnMetrics,
    dimensions: model.columnDimensions,
    xAxis: model.columnX,
    showDimensions: model.columnShowDimensions,
    rawColumns: model.columnRawColumns,
  },
  customDimensionMap: model.customDimensionMap,
  chartType: model.chartType,
  mode: model.mode,
  // 浅拷贝：避免 Sortable 直接 mutate store 内 dragState 引用
  dragState: { ...model.dragState },
  rowLimit: model.rowLimit,
  taskId: model.task_id,
  visibleSearch: model.visibleSearch,
  dataTables: model.dataTables,
  customColumns: model.customColumns,
  dataTableJoinRelations: model.dataTableJoinRelations,
  system: model.system,
  hasUserFilters: model.hasUserFilters,
  saveColumnInfos: model.save_column_infos,
  analysisRunning: model.analysisRunning,
  loading: ctx.loading,
  submitLoading: ctx.submitLoading,
});

/** connect mapStateToProps：保持与 PR1 前 flat props 一致，降低组件改动面 */
export const selectSearchConnectedProps = (state: {
  loading?: { effects?: Record<string, boolean>; };
  [namespace]?: ISearchModelState;
}): ISearchConnectedProps => {
  const model = selectSearchModelState(state);
  const effects = state.loading?.effects ?? {};

  return {
    dataTables: model.dataTables,
    columnFilters: model.columnFilters,
    columnUserFilters: model.columnUserFilters,
    columnCompares: model.columnCompares,
    columnMetrics: model.columnMetrics,
    columnDimensions: model.columnDimensions,
    columnX: model.columnX,
    columnShowDimensions: model.columnShowDimensions,
    customDimensionMap: model.customDimensionMap,
    columnRawColumns: model.columnRawColumns,
    mode: model.mode,
    dragState: { ...model.dragState },
    saveObj: model.saveObj,
    task_id: model.task_id,
    hashEqualForAnalysis: model.hashEqualForAnalysis,
    analysisRunning: model.analysisRunning,
    loading: Boolean(effects[`${namespace}/getTableList`]),
    submitLoading: Boolean(effects[`${namespace}/getDataAnlysis`]),
    chartType: model.chartType,
    rowLimit: model.rowLimit,
    save_column_infos: model.save_column_infos,
    customColumns: model.customColumns,
    dataTableJoinRelations: model.dataTableJoinRelations,
    system: model.system,
    hasUserFilters: model.hasUserFilters,
    visibleSearch: model.visibleSearch,
  };
};
